/**
 * Heating Safety Controls Component
 * Two permanent visible toggles:
 * 1. Verrou Chauffage (Heating Lock) - Blocks all heating
 * 2. Maison Vide/Occupée (House Occupancy) - Empty house = no heating
 * + Crazy Mode indicator
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Lock, Unlock, Home, DoorOpen, AlertTriangle, Shield, Power, Zap } from 'lucide-react';
import { useCrazyMode } from './CrazyMode';

const HeatingSafetyControls = ({ compact = false }) => {
  const { crazyMode } = useCrazyMode();
  const [status, setStatus] = useState({
    heating_locked: true,
    house_occupied: true,
    heating_allowed: false,
    last_safety_check: null,
    heaters_forced_off: 0
  });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const response = await api.get('/heating/safety/status');
      if (response.data?.success) {
        setStatus(response.data);
      }
    } catch (error) {
      console.error('Error loading heating safety status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Refresh every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  const toggleHeatingLock = async () => {
    setToggling('lock');
    try {
      const response = await api.post('/heating/safety/lock', {
        locked: !status.heating_locked
      });
      if (response.data?.success) {
        setStatus(prev => ({
          ...prev,
          heating_locked: response.data.heating_locked,
          heating_allowed: !response.data.heating_locked && prev.house_occupied
        }));
      }
    } catch (error) {
      console.error('Error toggling heating lock:', error);
    } finally {
      setToggling(null);
    }
  };

  const toggleHouseOccupancy = async () => {
    setToggling('occupancy');
    try {
      const response = await api.post('/heating/safety/occupancy', {
        occupied: !status.house_occupied
      });
      if (response.data?.success) {
        setStatus(prev => ({
          ...prev,
          house_occupied: response.data.house_occupied,
          heating_allowed: !prev.heating_locked && response.data.house_occupied
        }));
      }
    } catch (error) {
      console.error('Error toggling house occupancy:', error);
    } finally {
      setToggling(null);
    }
  };

  const forceAllOff = async () => {
    setToggling('force');
    try {
      const response = await api.post('/heating/safety/force-off');
      if (response.data?.success) {
        alert(`${response.data.forced_off} appareils éteints`);
        loadStatus();
      }
    } catch (error) {
      console.error('Error forcing heaters off:', error);
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-800/50 rounded-lg p-2">
        <div className="h-8 bg-gray-700 rounded"></div>
      </div>
    );
  }

  // Compact version for sidebar/header
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {/* Heating Lock Toggle */}
        <button
          onClick={toggleHeatingLock}
          disabled={toggling === 'lock'}
          data-testid="heating-lock-toggle"
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono transition-all ${
            status.heating_locked
              ? 'bg-red-900/50 border border-red-500 text-red-400'
              : 'bg-green-900/50 border border-green-500 text-green-400'
          } ${toggling === 'lock' ? 'opacity-50' : 'hover:scale-105'}`}
          title={status.heating_locked ? 'Verrou ACTIF - Cliquer pour déverrouiller' : 'Chauffage DÉVERROUILLÉ - Cliquer pour verrouiller'}
        >
          {status.heating_locked ? (
            <Lock className="w-3 h-3" />
          ) : (
            <Unlock className="w-3 h-3" />
          )}
          <span className="hidden sm:inline">
            {status.heating_locked ? 'VERROU' : 'LIBRE'}
          </span>
        </button>

        {/* House Occupancy Toggle */}
        <button
          onClick={toggleHouseOccupancy}
          disabled={toggling === 'occupancy'}
          data-testid="house-occupancy-toggle"
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono transition-all ${
            status.house_occupied
              ? 'bg-cyan-900/50 border border-cyan-500 text-cyan-400'
              : 'bg-orange-900/50 border border-orange-500 text-orange-400'
          } ${toggling === 'occupancy' ? 'opacity-50' : 'hover:scale-105'}`}
          title={status.house_occupied ? 'Maison OCCUPÉE - Cliquer pour marquer vide' : 'Maison VIDE - Cliquer pour marquer occupée'}
        >
          {status.house_occupied ? (
            <Home className="w-3 h-3" />
          ) : (
            <DoorOpen className="w-3 h-3" />
          )}
          <span className="hidden sm:inline">
            {status.house_occupied ? 'OCCUPÉ' : 'MAISON VIDE'}
          </span>
        </button>

        {/* CRAZY MODE Indicator - Flashing */}
        {crazyMode && (
          <div 
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono font-bold animate-pulse"
            style={{
              background: 'linear-gradient(90deg, #7c3aed 0%, #ec4899 50%, #7c3aed 100%)',
              backgroundSize: '200% 100%',
              animation: 'crazy-flash 1s ease-in-out infinite',
              border: '1px solid #a855f7',
              color: '#fff',
              textShadow: '0 0 5px #fff'
            }}
            data-testid="crazy-mode-indicator"
          >
            <Zap className="w-3 h-3" />
            <span className="hidden sm:inline">CRAZY MODE</span>
          </div>
        )}
      </div>
    );
  }

  // Full version for dashboard panel
  return (
    <div 
      className="bg-black/60 border border-cyan-500/50 rounded-lg p-3"
      style={{ boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)' }}
      data-testid="heating-safety-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <Shield className="w-4 h-4 text-cyan-400 mr-2" />
          <span className="text-xs font-bold text-cyan-400 font-mono">SÉCURITÉ CHAUFFAGE</span>
        </div>
        {/* Status indicator */}
        <div className={`flex items-center text-[10px] font-mono ${
          status.heating_allowed ? 'text-green-400' : 'text-red-400'
        }`}>
          <span className={`w-2 h-2 rounded-full mr-1 ${
            status.heating_allowed ? 'bg-green-500 animate-pulse' : 'bg-red-500'
          }`}></span>
          {status.heating_allowed ? 'AUTORISÉ' : 'BLOQUÉ'}
        </div>
      </div>

      {/* Two main toggles */}
      <div className="space-y-2">
        {/* Heating Lock */}
        <button
          onClick={toggleHeatingLock}
          disabled={toggling === 'lock'}
          data-testid="heating-lock-btn"
          className={`w-full flex items-center justify-between p-2 rounded-lg transition-all ${
            status.heating_locked
              ? 'bg-red-900/30 border border-red-500/50 hover:bg-red-900/50'
              : 'bg-green-900/30 border border-green-500/50 hover:bg-green-900/50'
          } ${toggling === 'lock' ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center">
            {status.heating_locked ? (
              <Lock className="w-5 h-5 text-red-400 mr-2" />
            ) : (
              <Unlock className="w-5 h-5 text-green-400 mr-2" />
            )}
            <div className="text-left">
              <div className={`text-sm font-bold font-mono ${
                status.heating_locked ? 'text-red-400' : 'text-green-400'
              }`}>
                VERROU CHAUFFAGE
              </div>
              <div className="text-[9px] text-gray-500">
                {status.heating_locked 
                  ? 'Tous chauffages BLOQUÉS'
                  : 'Chauffages autorisés'
                }
              </div>
            </div>
          </div>
          <div className={`text-xs font-bold font-mono px-2 py-1 rounded ${
            status.heating_locked
              ? 'bg-red-500 text-white'
              : 'bg-green-500 text-black'
          }`}>
            {status.heating_locked ? 'ON' : 'OFF'}
          </div>
        </button>

        {/* House Occupancy */}
        <button
          onClick={toggleHouseOccupancy}
          disabled={toggling === 'occupancy'}
          data-testid="house-occupancy-btn"
          className={`w-full flex items-center justify-between p-2 rounded-lg transition-all ${
            status.house_occupied
              ? 'bg-cyan-900/30 border border-cyan-500/50 hover:bg-cyan-900/50'
              : 'bg-orange-900/30 border border-orange-500/50 hover:bg-orange-900/50'
          } ${toggling === 'occupancy' ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center">
            {status.house_occupied ? (
              <Home className="w-5 h-5 text-cyan-400 mr-2" />
            ) : (
              <DoorOpen className="w-5 h-5 text-orange-400 mr-2" />
            )}
            <div className="text-left">
              <div className={`text-sm font-bold font-mono ${
                status.house_occupied ? 'text-cyan-400' : 'text-orange-400'
              }`}>
                MAISON
              </div>
              <div className="text-[9px] text-gray-500">
                {status.house_occupied 
                  ? 'Occupée - chauffage possible'
                  : 'Vide - chauffage désactivé'
                }
              </div>
            </div>
          </div>
          <div className={`text-xs font-bold font-mono px-2 py-1 rounded ${
            status.house_occupied
              ? 'bg-cyan-500 text-black'
              : 'bg-orange-500 text-white'
          }`}>
            {status.house_occupied ? 'OCCUPÉ' : 'MAISON VIDE'}
          </div>
        </button>
      </div>

      {/* Warning if heating blocked */}
      {!status.heating_allowed && (
        <div className="mt-2 flex items-center p-2 bg-yellow-900/30 border border-yellow-500/30 rounded text-[10px] text-yellow-400">
          <AlertTriangle className="w-3 h-3 mr-1 flex-shrink-0" />
          <span>
            {status.heating_locked && !status.house_occupied
              ? 'Verrou + Maison vide: Tout chauffage impossible'
              : status.heating_locked
              ? 'Verrou actif: Automations bloquées'
              : 'Maison vide: Chauffage désactivé'
            }
          </span>
        </div>
      )}

      {/* Emergency button */}
      <button
        onClick={forceAllOff}
        disabled={toggling === 'force'}
        data-testid="force-off-btn"
        className="mt-2 w-full flex items-center justify-center gap-1 p-1.5 bg-red-900/50 border border-red-600 rounded text-xs text-red-400 hover:bg-red-900/70 transition-all"
      >
        <Power className="w-3 h-3" />
        ÉTEINDRE TOUT
      </button>
    </div>
  );
};

export default HeatingSafetyControls;
